import { FormEvent } from 'react';
import styled from 'styled-components';

import { DEPOSITS_WITHDRAWALS_DOCS_URL } from '#/routes-external';

import InProgressIcon from '#/assets/InProgressIcon';

import * as Alert from '#/components/common/Alert';
import { ShowMoreText } from '#/components/shared/ShowMoreText';
import ExternalLink from '#/components/shared/ExternalLink';

import { logException } from '#/features/logging/logging';
import {
  useWithdrawFormActions,
  useWithdrawFormView,
} from '#/features/withdraw/form-context';
import { useWithdrawModalActions } from '#/features/withdraw/modal-context';
import {
  useWithdrawActions,
  useWithdrawView,
} from '#/features/withdraw/withdraw-context';
import { useAccountView } from '#/features/account/account-context';
import { publishNotification } from '#/features/notifications/topic';
import { Translation, useTranslation } from '#/features/localization/utils';

import AccountBalance from './AccountBalance';
import InputAmount from './InputAmount';
import InputAsset from './InputAsset';
import Button from './Button';

export default function WithdrawForm() {
  const formView = useWithdrawFormView();
  const formActions = useWithdrawFormActions();
  const withdrawView = useWithdrawView();
  const withdrawActions = useWithdrawActions();
  const modalActions = useWithdrawModalActions();
  const accountView = useAccountView();
  const { t } = useTranslation();

  const isDisabled =
    !formView.isValid || withdrawView.isBusy || accountView.isBeingLiquidated;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formView.isValid) {
      throw new Error('Withdraw form is not valid');
    }

    (async function initiateWithdraw() {
      const amount = formView.amount.value;

      const result = await withdrawActions.initiateWithdraw(
        amount,
        formView.asset.symbol,
      );

      if (!result.ok) {
        publishNotification({
          kind: 'initiate-withdraw-request-failed',
          props: {},
        });
        return;
      }

      const amountFormatted = formView.amount.formatted;
      publishNotification({
        kind: 'initiate-withdraw-request-sent',
        props: { amount: amountFormatted },
      });

      formActions.reset();
      modalActions.close();
    })().catch((err) => {
      publishNotification({
        kind: 'withdraw-unexpected-error',
        props: {},
      });
      const message = 'Unexpected error in withdraw submit handler';
      logException(new Error(message, { cause: err }));
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <InputAsset />
      <InputAmount />
      {formView.error.length > 0 && <Alert.Error>{formView.error}</Alert.Error>}
      <Info />
      {withdrawView.error.length > 0 && (
        <Alert.Error>
          <ShowMoreText maxLength={85}>{withdrawView.error}</ShowMoreText>
        </Alert.Error>
      )}
      <AccountBalance />
      <Button type="submit" disabled={isDisabled}>
        {withdrawView.isBusy ? (
          <InProgressIcon size={22} />
        ) : (
          t('Initiate Withdrawal to Bridge')
        )}
      </Button>
    </Form>
  );
}

const Info = () => {
  const { t } = useTranslation();
  return (
    <InfoContainer>
      <ShowMoreText
        preview={t(
          'Withdrawals are a two-step process. First initiate a withdrawal from Paraclear',
        )}
      >
        <Translation
          t={t}
          i18nKey="Withdrawals are a two-step process. First initiate a withdrawal from Paraclear to the Starknet bridge. This first step can take 2-7 hours. Second, initiate your withdrawal from the Starknet bridge to your Ethereum wallet via the Deposit / Withdraw table. For more information check out our <1>docs</1>."
          components={{
            1: <ExternalLink href={DEPOSITS_WITHDRAWALS_DOCS_URL} />,
          }}
        />
      </ShowMoreText>
    </InfoContainer>
  );
};

const Form = styled.form`
  display: grid;
  gap: 18px;
`;

const InfoContainer = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  line-height: 18px;
`;
